import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { adminApi, messageFrom } from "@/lib/api";
import { setIn } from "./ratePaths";

/**
 * The rate card as the admin is editing it.
 *
 * `payload` is what the server last confirmed and `draft` is what is on screen;
 * comparing the two is what makes the Save button light up. Saving, resetting
 * and restoring an old version all land the same way - the server's answer
 * becomes both the new payload and the new draft, so the screen can never drift
 * from what is actually pricing quotes.
 */
export function useRateCard(onAuthError) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [payload, setPayload] = useState(null);
  const [draft, setDraft] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.rateCard();
      setPayload(data);
      setDraft(data.rates);
    } catch (error) {
      if (!onAuthError?.(error)) toast.error(messageFrom(error));
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () => Boolean(draft && payload) && JSON.stringify(draft) !== JSON.stringify(payload.rates),
    [draft, payload],
  );

  const accept = useCallback((data, { logged = false } = {}) => {
    setPayload((current) => ({ ...current, ...data }));
    setDraft(data.rates);
    if (logged) setHistoryKey((key) => key + 1);
  }, []);

  const change = useCallback(
    (path, value) => setDraft((current) => setIn(current, path, value)),
    [],
  );

  /** Run a write, keep the screen in step with the answer, and say so. */
  const commit = useCallback(
    async (flag, work, message) => {
      flag(true);
      try {
        accept(await work(), { logged: true });
        toast.success(message);
      } catch (error) {
        if (!onAuthError?.(error)) toast.error(messageFrom(error));
      } finally {
        flag(false);
      }
    },
    [accept, onAuthError],
  );

  const save = () =>
    commit(
      setSaving,
      () => adminApi.saveRateCard(draft),
      "Rate card saved — new quotes use these figures immediately.",
    );

  const reset = () =>
    commit(
      setResetting,
      () => adminApi.resetRateCard(),
      "Rate card restored to the figures the calculator shipped with.",
    );

  return {
    loading,
    saving,
    resetting,
    payload,
    draft,
    dirty,
    historyKey,
    load,
    change,
    save,
    reset,
    onRestored: accept,
  };
}
